import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import projectService from "../services/projectService";
import authService from "../services/authService";
import "../styles/Projects.css";

function MyProjects() {
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [licenseStatus, setLicenseStatus] = useState({
    valid: false,
    expiry: null,
    message: "Checking license..."
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = await authService.getMe();
        if (user) {
          // Check License
          if (!user.licenseExpiry) {
            setLicenseStatus({
              valid: false,
              expiry: null,
              message: "No active license found. Please contact admin."
            });
          } else {
            const expiryDate = new Date(user.licenseExpiry);
            const now = new Date();
            // Check if expired (considering end of day or exact time, simple comparison works)
            const isValid = expiryDate > now;

            setLicenseStatus({
              valid: isValid,
              expiry: user.licenseExpiry,
              message: isValid ? "License Active" : "License Expired"
            });
          }

          // 1. Fetch Assigned Projects (Verified/Working)
          const myProjects = await projectService.fetchMyProjects(user.uid);
          setAssignedProjects(myProjects);

          // 2. Fetch Pending Requests
          const requests = await projectService.fetchUserRequests(user.uid);

          // Filter out requests for projects that are already assigned (approved)
          // Also filter out rejected requests if desired, or show them with status
          const activeProjectIds = new Set(myProjects.map(p => p._id));

          const pendingProjectsPromises = requests
            .filter(req => req.status === 'pending' && !activeProjectIds.has(req.projectId))
            .map(async (req) => {
              try {
                const project = await projectService.fetchProjectById(req.projectId);
                return { ...project, requestStatus: 'pending' };
              } catch (e) {
                return null;
              }
            });

          const pendingProjects = (await Promise.all(pendingProjectsPromises)).filter(p => p !== null);
          setPendingRequests(pendingProjects);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const ProjectCard = ({ project, statusLabel, statusColor }) => (
    <div className="project-card">
      {project.image && (
        <img src={project.image} alt={project.title} className="project-image" />
      )}
      <div className="project-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <h3>{project.title}</h3>
          <span style={{
            fontSize: '0.8rem',
            padding: '0.2rem 0.6rem',
            borderRadius: '100px',
            background: statusColor,
            color: 'white',
            fontWeight: '600'
          }}>
            {statusLabel}
          </span>
        </div>
        <p className="project-desc">{project.description}</p>

        <div className="project-meta">
          <span>💰 ₹{project.budget?.toLocaleString()}</span>
          <span>📅 {project.deadline}</span>
        </div>

        {/* Redirect button for active projects */}
        {/* Redirect button for active projects */}
        {statusLabel === 'Active' && (
          <a
            href="https://dashboard.scale.com/login?redirect_url=%2Fstudio%2Fannotate%3FstudioProjectId%3D6912dbb3810162f7560e33e8&clear=1"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block',
              marginTop: '1rem',
              padding: '0.8rem',
              background: 'var(--accent-primary)',
              borderRadius: '6px',
              color: 'white',
              textAlign: 'center',
              textDecoration: 'none',
              fontWeight: '600',
              transition: 'background 0.2s'
            }}
          >
            Start Tasking ↗
          </a>
        )}

        {statusLabel === 'Pending' && (
          <div style={{ marginTop: '1rem', padding: '0.5rem', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '4px', color: '#d46b08', fontSize: '0.9rem', textAlign: 'center' }}>
            ⏳ Waiting for admin verification
          </div>
        )}

      </div>
    </div>
  );

  return (
    <div className="projects-page">
      <Sidebar />
      <div className="projects-container">
        <h2 className="page-title">My Projects (Personal Workspace)</h2>
        <p className="page-subtitle">Your active projects and pending requests.</p>

        {/* Global Redirect Button */}


        {loading ? (
          <p>Loading...</p>
        ) : !licenseStatus.valid ? (
          <div className="license-alert" style={{
            background: '#fff1f0',
            border: '1px solid #ffccc7',
            padding: '2rem',
            borderRadius: '8px',
            textAlign: 'center',
            marginTop: '2rem'
          }}>
            <h3 style={{ color: '#cf1322', marginBottom: '1rem' }}>⚠️ Access Restricted</h3>
            <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>{licenseStatus.message}</p>
            {licenseStatus.expiry && <p>Expired on: {new Date(licenseStatus.expiry).toLocaleDateString()}</p>}
            <p style={{ marginTop: '1rem', fontWeight: '500' }}>Please renew your subscription to access your workspace.</p>
          </div>
        ) : (
          <>
            <div className="section-header" style={{ marginBottom: '1.5rem', marginTop: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', color: 'var(--text-primary)' }}>Active Projects</h3>
            </div>

            <div className="projects-grid">
              {assignedProjects.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)' }}>No active projects yet. Check Available Projects to grab one!</p>
              ) : (
                assignedProjects.map(project => (
                  <ProjectCard
                    key={project._id}
                    project={project}
                    statusLabel="Active"
                    statusColor="#52c41a"
                  />
                ))
              )}
            </div>

            <div className="section-header" style={{ marginBottom: '1.5rem', marginTop: '3rem', borderTop: '1px solid var(--border-color)', paddingTop: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', color: 'var(--text-primary)' }}>Pending Requests</h3>
            </div>

            <div className="projects-grid">
              {pendingRequests.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)' }}>No pending requests.</p>
              ) : (
                pendingRequests.map(project => (
                  <ProjectCard
                    key={project._id}
                    project={project}
                    statusLabel="Pending"
                    statusColor="#faad14"
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MyProjects;
